import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import * as sequtil from 'molbio/SeqUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';

export interface FindPrimersRowProps {
  /** An HTML string representing the primer's bases, the binding site, and which bases are exact matches */
  bases: string;
  /** Whether the row's checkbox should be checked */
  checked: boolean;
  /** Whether the row should be disabled */
  disabled: boolean;
  /** The end of the binding site, relative to the sequence's forward strand */
  end: number;
  /** The melting temperature of the primer */
  meltingTemperature: string;
  /** The nucleotide type of the primer */
  nucleotideType: NucleotideType;
  /** The name of the primer entity */
  name: string;
  /** A function to be called when the row's checkbox is toggled. Called with the new checked state. */
  onChange: (e: boolean) => void;
  /** A function to be called when the row, but not the checkbox or the edit button, is clicked */
  onClick: () => void;
  /** A function to be called when the edit button is clicked */
  onClickEdit: () => void;
  /** The API id of the primer, if it exists */
  primerApiId: string | null;
  /** The length of the sequence the primer search is being performed over */
  seqLength: number;
  /** Whether to show the name of the primer; true when searching over folders or worklists */
  showName: boolean;
  /** The start of the binding site, relative to the sequence's forward strand */
  start: number;
  /** The start index of the sequence the primer search is being performed over */
  startIndex: number;
  /** What strand the binding site is on */
  strand: StrandOfDoubleStranded;
}

export default function FindPrimersRow({
  bases,
  checked,
  disabled,
  end,
  meltingTemperature,
  nucleotideType,
  name,
  onChange,
  onClick,
  onClickEdit,
  primerApiId,
  seqLength,
  showName,
  start,
  startIndex,
  strand,
}: FindPrimersRowProps): JSX.Element {
  // We have to provide a unique ID to associate the checkbox with its (hidden) label. In the 'arbitrary
  // bases' case, the primer won't have an API ID or name, but will have a unique location.
  const checkboxIdSuffix = primerApiId ?? `${strand}-${start}`;
  const checkboxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (checkboxRef.current != null && !checkboxRef.current.classList.contains('include-primer')) {
      checkboxRef.current.classList.add('include-primer');
    }
  });

  return (
    <tr
      onClick={(e) => {
        if (e.target !== checkboxRef.current) {
          onClick();
        }
      }}
      className={classNames(css(styles.wrapper), {
        active: checked,
        'disabled success': disabled,
      })}
    >
      <td>
        <CheckboxInput
          label="Select primer"
          labelStyle={LabelStyle.HIDDEN}
          id={`find-primers-result-${checkboxIdSuffix}`}
          inputRef={checkboxRef}
          checked={checked && !disabled}
          disabled={disabled}
          onChange={(e) => {
            if (!disabled) {
              onChange(e.target.checked);
            }
          }}
          dataTestAttrs={{component: 'FindPrimersRow', element: 'checkbox', key: name}}
        />
      </td>
      {showName && (
        <td
          {...getTestProps({component: 'FindPrimersRow', element: 'primerName', key: name})}
          data-amp-safe={false}
        >
          {name}
        </td>
      )}
      <td>{nucleotideType}</td>
      {strand === 1 ? (
        <td>+ / {sequtil.modelEndToDisplay(end, seqLength, startIndex)}</td>
      ) : (
        <td>&ndash; / {sequtil.modelStartToDisplay(start, startIndex)}</td>
      )}
      <td>{meltingTemperature}</td>
      <td
        className={classNames(css(styles.bases), 'sequence-primer-bases', 'mono')}
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{__html: bases}}
      />
      <td>
        {!disabled && (
          <a
            className="edit"
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit();
            }}
          >
            Edit
          </a>
        )}
      </td>
    </tr>
  );
}

const styles = StyleSheet.create({
  bases: {width: '100%'},
  wrapper: {cursor: 'pointer'},
});
